import { useEffect, useMemo, useState } from "react";
import { useOpenSessionByDefault } from "../../application/useOpenSessionByDefault";
import { useMentoringCourses } from "../../application/useMentoringCourses";
import { isSubjectOffered, useCourseLabels, useSessionPreferences } from "../../../center";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
    CalendarDays,
    CalendarRange,
    List,
    Plus,
} from "lucide-react";

import { useTherapist } from "../../../../context/useTherapist";
import MonthAgendaGrid from "../../../../shared/ui/calendar/MonthAgendaGrid";
import { BRAND_BOOKING_COLOR } from "../../../../shared/ui/calendar/bookingCardStyle";
import { minuteLabel } from "../../../../shared/domain/calendarGrid";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { useGroupPatients, groupLabel } from "../../../patient";
import { listSchedulerCouples, listSchedulerPatients } from "../../application/schedulerCaseload";
import {
    useAvailability,
    useCancelSession,
    useRescheduleSession,
    useScheduledSessions,
    useScheduleSession,
    useSchedulerSettings,
    useSpaces,
} from "../../application/useScheduler";
import {
    detectBrowserTimezone,
    endOfWeekKey,
    localTimeOfDay,
    shiftDateKey,
    startOfWeekKey,
    toDateKey,
    toMonthKey,
} from "../../domain/formatting";
import { isSpanPast, minutesOfDayTime } from "../../domain/weekLayout";
import { ScheduledSession, ScheduledStatus, SessionUbication } from "../../domain/types";
import ScheduledSessionCard from "../components/ScheduledSessionCard";
import ScheduleSessionModal from "../components/ScheduleSessionModal";
import RescheduleSessionModal from "../components/RescheduleSessionModal";
import { useAlchelyMeetingAdopted } from "../../../marketplace";
import { recordManualWhatsAppReminder } from "../../../session";
import WeekGrid, { OpenedSession } from "../components/WeekGrid";
import type { PatientOption } from "../../../../shared/ui/patient/PatientFinder";
import { subjectLabel } from "../../domain/subject";
import {
    fillReminderTemplate,
    sessionReminderWhen,
    whatsappChatUrl,
    whatsappDigits,
    type WhatsAppReminderAction,
} from "../../domain/whatsappReminder";

type ViewMode = "list" | "week" | "month";


export default function SchedulerCalendarPage() {
    const { t, i18n } = useTranslation();
    const openSessionByDefault = useOpenSessionByDefault();
    const mentoringCourses = useMentoringCourses();
    const { therapist } = useTherapist();

    // Week first: it is the view that answers "how does my week actually look",
    // which is what a therapist arranging sessions opens the scheduler for.
    const [viewMode, setViewMode] = useState<ViewMode>("week");
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [weekStart, setWeekStart] = useState(() =>
        startOfWeekKey(toDateKey(new Date(), detectBrowserTimezone()))
    );
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    /**
     * The booking opened from the month, shown in the week's own sheet. Held by
     * id and read from the month's rows, so a cancel or a reschedule shows in
     * the open sheet rather than the booking as it was when opened.
     */
    const [openedSessionId, setOpenedSessionId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [bookingError, setBookingError] = useState<string | null>(null);
    /** The hour clicked in the week grid, so the modal opens on that time. */
    const [bookingMinute, setBookingMinute] = useState<number | null>(null);
    const [sessionToCancel, setSessionToCancel] =
        useState<ScheduledSession | null>(null);
    // Whether the patient asked for it: what the reports count as owed.
    const [requestedByPatient, setRequestedByPatient] = useState(false);
    const [sessionToReschedule, setSessionToReschedule] =
        useState<ScheduledSession | null>(null);
    // List view only, and on by default: the list is what a therapist opens to
    // see what is coming, and a month already half spent otherwise buries it
    // under sessions that have been and gone.
    const [hidePastSessions, setHidePastSessions] = useState(true);
    const [rescheduleError, setRescheduleError] = useState<string | null>(null);
    // Only so the week grid's "now" line moves without a reload.
    const [now, setNow] = useState(() => new Date());

    const month = toMonthKey(currentMonth);
    const isWeekView = viewMode === "week";
    const weekEnd = endOfWeekKey(weekStart);
    const weekMonth = weekStart.slice(0, 7);
    const weekEndMonth = weekEnd.slice(0, 7);

    const settingsQuery = useSchedulerSettings();
    const spacesQuery = useSpaces();
    const availabilityQuery = useAvailability();
    // The list and month views read one month; a week can straddle two, and the
    // API is keyed by month, so the second one is fetched only when it differs.
    // Cancelled bookings come too: they stay on the agenda, marked as such,
    // so one can be rescheduled rather than booked again from scratch.
    const sessionsQuery = useScheduledSessions(isWeekView ? "" : month, true);
    const weekQuery = useScheduledSessions(isWeekView ? weekMonth : "", true);
    const weekSpillQuery = useScheduledSessions(
        isWeekView && weekEndMonth !== weekMonth ? weekEndMonth : "",
        true
    );
    const scheduleSession = useScheduleSession();
    const alchelyMeetingAvailable = useAlchelyMeetingAdopted();
    const cancelSession = useCancelSession();
    const rescheduleSession = useRescheduleSession();

    const activeQueries = isWeekView
        ? [weekQuery, weekSpillQuery]
        : [sessionsQuery];
    const sessionsLoading = activeQueries.some((query) => query.isLoading);
    const sessionsFailed = activeQueries.some((query) => query.isError);
    // Only the single-month views read this, so a held second week query never
    // makes it false: an error must not also read as "nothing scheduled".
    const sessionsLoaded = activeQueries.every((query) => query.isSuccess);
    const refetchSessions = () =>
        activeQueries.forEach((query) => query.refetch());

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(timer);
    }, []);

    // Null until known, so a time is never labelled with a guessed zone. The
    // date helpers still need something, and "today" is the same day in almost
    // every zone, so they fall back locally.
    const timezone = settingsQuery.data?.timezone ?? null;
    const zone = timezone ?? detectBrowserTimezone();
    const todayKey = toDateKey(now, zone);
    const nowMinute = minutesOfDayTime(localTimeOfDay(now, zone));

    const patientsQuery = useQuery({
        queryKey: ["scheduler-patients", therapist?.id],
        queryFn: () => listSchedulerPatients(therapist?.id as string),
        enabled: Boolean(therapist?.id),
    });

    const couplesQuery = useQuery({
        queryKey: ["scheduler-couples", therapist?.id],
        queryFn: () => listSchedulerCouples(therapist?.id as string),
        enabled: Boolean(therapist?.id),
    });

    const { groups } = useGroupPatients(Boolean(therapist?.id));

    // Individuals first, then couples, then groups: a therapist looking for
    // one person should not have to scroll past the pairs to reach them.
    // Couples and groups carry who is in them, so searching for one member
    // finds the pair or the group as well as the person.
    // A training centre's student, with the course they are on under the name.
    const { courseOf } = useCourseLabels();
    const patientOptions = useMemo<PatientOption[]>(() => {
        const fullName = (person: { first_name: string; last_name: string }) =>
            `${person.first_name} ${person.last_name}`.trim();
        const nameById = new Map(
            (patientsQuery.data ?? []).map((patient) => [patient.id, fullName(patient)])
        );
        return [
            ...(patientsQuery.data ?? []).map((patient) => ({
                id: patient.id,
                name: fullName(patient),
                kind: "patient" as const,
                courseId: patient.course_id ?? null,
                detail: courseOf(patient.course_id),
            })),
            ...(couplesQuery.data ?? []).map((couple) => ({
                id: couple.id,
                name: couple.display_name,
                kind: "couple" as const,
                members: [
                    couple.patient_1 ? fullName(couple.patient_1) : nameById.get(couple.patient_1_id),
                    couple.patient_2 ? fullName(couple.patient_2) : nameById.get(couple.patient_2_id),
                ].filter((name): name is string => Boolean(name)),
            })),
            ...groups.map((group) => ({
                id: group.id,
                name: groupLabel(group),
                kind: "group" as const,
                members: group.members.map(fullName),
            })),
        ];
    }, [patientsQuery.data, couplesQuery.data, groups, courseOf]);

    // Only the kinds of session the centre runs are offered to book; the names
    // below still cover every kind, for bookings made before one was switched off.
    const { modes } = useSessionPreferences();
    const bookableOptions = useMemo(
        () => patientOptions.filter((option) => isSubjectOffered(modes, option.kind)),
        [patientOptions, modes]
    );

    // Keyed by subject id, so a couple booking — which carries no patient_id —
    // still resolves to a name instead of rendering blank.
    const patientNames = useMemo(() => {
        const lookup = new Map<string, string>();
        patientOptions.forEach((option) => lookup.set(option.id, option.name));
        return lookup;
    }, [patientOptions]);

    const patientsById = useMemo(
        () =>
            new Map(
                (patientsQuery.data ?? []).map((patient) => [patient.id, patient])
            ),
        [patientsQuery.data]
    );

    /**
     * The WhatsApp button of a booking: a `wa.me` link to the patient's chat,
     * with a reminder of the session and its join link already written for
     * the therapist to send — always to the patient's WhatsApp number, never
     * their contact phone. The therapist sends it from their own WhatsApp,
     * so it does not wait on the patient's opt-in: that switch governs the
     * automatic messages only.
     *
     * Individual patients only: a couple or a group has no single chat to open.
     */
    const whatsappReminder = (
        session: ScheduledSession
    ): WhatsAppReminderAction | undefined => {
        const patient = session.patient_id
            ? patientsById.get(session.patient_id)
            : undefined;
        if (!patient) return undefined;
        const digits = whatsappDigits(patient.whatsapp_phone, patient.country);
        if (!digits) return undefined;
        const when = sessionReminderWhen(
            session.date.slice(0, 10),
            session.hour,
            timezone,
            patient.timezone,
            i18n.language
        );
        if (!when) return undefined;

        const template =
            settingsQuery.data?.whatsapp_reminder_template ||
            t("scheduler.schedule.whatsappReminderDefaultTemplate");
        const link =
            session.ubication === SessionUbication.ONLINE
                ? (session.meeting_link ?? "")
                : "";
        const sessionId = session.session_id;
        return {
            kind: "open",
            onOpen: sessionId ? () => recordManualWhatsAppReminder(sessionId) : undefined,
            url: whatsappChatUrl(
                digits,
                fillReminderTemplate(template, {
                    name: patient.first_name,
                    date: when.date,
                    time: when.time,
                    link,
                })
            ),
        };
    };

    const spaceNames = useMemo(() => {
        const lookup = new Map<string, string>();
        (spacesQuery.data ?? []).forEach((space) =>
            lookup.set(space.id, space.name)
        );
        return lookup;
    }, [spacesQuery.data]);

    const visibleSessions = useMemo(
        () =>
            isWeekView
                ? [...(weekQuery.data ?? []), ...(weekSpillQuery.data ?? [])]
                : (sessionsQuery.data ?? []),
        [isWeekView, weekQuery.data, weekSpillQuery.data, sessionsQuery.data]
    );

    /** `YYYY-MM-DD` → the day's sessions, ascending by time. */
    const sessionsByDay = useMemo(() => {
        const byDay = new Map<string, ScheduledSession[]>();
        visibleSessions.forEach((session) => {
            const day = session.date.slice(0, 10);
            byDay.set(day, [...(byDay.get(day) ?? []), session]);
        });
        byDay.forEach((sessions) =>
            sessions.sort((a, b) => a.hour.localeCompare(b.hour))
        );
        return byDay;
    }, [visibleSessions]);

    const openedSession =
        viewMode === "month" && openedSessionId
            ? (visibleSessions.find((session) => session.id === openedSessionId) ?? null)
            : null;

    const orderedDays = useMemo(
        () => [...sessionsByDay.keys()].sort((a, b) => a.localeCompare(b)),
        [sessionsByDay]
    );

    // A day selected in one month is meaningless once the month changes.
    // Drop the selection only once it no longer belongs to the month on screen,
    // so deliberately jumping to a day in another month survives.
    useEffect(() => {
        if (selectedDay?.startsWith(month)) return;
        setSelectedDay(null);
    }, [month, selectedDay]);

    // An opened booking belongs to the month and the view it was opened from.
    useEffect(() => {
        setOpenedSessionId(null);
    }, [month, viewMode]);

    /** Bring a specific day into view, changing month if it is elsewhere. */
    const revealDay = (isoDate: string) => {
        const day = isoDate.slice(0, 10);
        const [year, monthNumber] = day.split("-").map(Number);
        setCurrentMonth(new Date(year, monthNumber - 1, 1));
        setSelectedDay(day);
    };

    /** The month on screen one on (`step` 1) or back (`step` -1). */
    const shiftMonth = (step: number) =>
        setCurrentMonth(
            (current) => new Date(current.getFullYear(), current.getMonth() + step, 1)
        );

    /**
     * Move the week on screen, dragging the month with it so switching views
     * lands on the stretch of time the therapist was just looking at.
     */
    const goToWeek = (start: string) => {
        setWeekStart(start);
        const [year, monthNumber] = start.split("-").map(Number);
        setCurrentMonth(new Date(year, monthNumber - 1, 1));
    };

    /** `1 – 7 Sept 2026`, dropping the parts the two ends share. */
    const weekRangeLabel = () => {
        const from = new Date(`${weekStart}T12:00:00`);
        const to = new Date(`${weekEnd}T12:00:00`);
        const sameMonth = weekStart.slice(0, 7) === weekEnd.slice(0, 7);
        const start = from.toLocaleDateString(i18n.language, {
            day: "numeric",
            month: sameMonth ? undefined : "short",
        });
        const end = to.toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        return `${start} – ${end}`;
    };

    /** Whether a booking has already finished, on the therapist's own clock. */
    const hasHappened = (session: ScheduledSession) => {
        const startMinute = minutesOfDayTime(session.hour);
        return isSpanPast(
            session.date.slice(0, 10),
            { startMinute, endMinute: startMinute + session.duration },
            todayKey,
            nowMinute
        );
    };

    /**
     * The list view's own grouping: past sessions optionally dropped.
     *
     * Separate from `sessionsByDay`, which the week and month views share —
     * hiding what has happened makes sense in a list read top to bottom, and
     * would punch holes in a grid that is meant to show a whole week.
     */
    const listSessionsByDay = useMemo(() => {
        if (!hidePastSessions) return sessionsByDay;
        const byDay = new Map<string, ScheduledSession[]>();
        sessionsByDay.forEach((sessions, day) => {
            const upcoming = sessions.filter((session) => !hasHappened(session));
            if (upcoming.length) byDay.set(day, upcoming);
        });
        return byDay;
        // `hasHappened` closes over the ticking clock, which is what should
        // re-run this; it is not stable across renders, so the inputs it reads
        // are the dependencies rather than the function itself.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionsByDay, hidePastSessions, todayKey, nowMinute]);

    /**
     * Days for the list, soonest first.
     *
     * Today and everything after it lead, ascending, so the next session is at
     * the top. Days already past follow in reverse, nearest first — when they
     * are shown at all, what matters is the session that just happened, not the
     * one at the start of the month.
     */
    const listDays = useMemo(() => {
        const days = [...listSessionsByDay.keys()];
        const upcoming = days.filter((day) => day >= todayKey).sort();
        const past = days.filter((day) => day < todayKey).sort().reverse();
        return [...upcoming, ...past];
    }, [listSessionsByDay, todayKey]);

    const openReschedule = (session: ScheduledSession) => {
        setRescheduleError(null);
        setSessionToReschedule(session);
    };

    const renderSession = (session: ScheduledSession) => (
        <ScheduledSessionCard
            key={session.id}
            session={session}
            timezone={timezone}
            patientName={
                subjectLabel(session, patientNames)
            }
            spaceName={
                session.space_id ? spaceNames.get(session.space_id) : undefined
            }
            isCancelling={cancelSession.isPending}
            isPast={hasHappened(session)}
            onCancel={(target) => setSessionToCancel(target)}
            onReschedule={(target) => openReschedule(target)}
            isRescheduling={rescheduleSession.isPending}
            whatsappReminder={whatsappReminder(session)}
        />
    );

    const formatDayHeading = (day: string) =>
        new Date(`${day}T12:00:00`).toLocaleDateString(i18n.language, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        });

    if (settingsQuery.isLoading) {
        return (
            <div className="p-6">
                <p role="status">{t("scheduler.common.loading")}</p>
            </div>
        );
    }

    if (settingsQuery.isError || !settingsQuery.data) {
        return (
            <div role="alert" className="flex items-center gap-2 p-6">
                <span>{t("scheduler.common.loadError")}</span>
                <button
                    type="button"
                    onClick={() => settingsQuery.refetch()}
                    className="underline"
                >
                    {t("scheduler.common.retry")}
                </button>
            </div>
        );
    }

    return (
        <div className="mx-auto flex max-w-6xl flex-col gap-4 p-4 sm:p-6">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-content-primary">
                        {t("scheduler.title")}
                    </h1>
                    <p className="mt-1 text-sm text-content-tertiary">
                        {timezone ? t("scheduler.calendar.timezoneNote", { timezone }) : ""}
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/10">
                        {(
                            [
                                { mode: "list", label: "listView", icon: List },
                                {
                                    mode: "week",
                                    label: "weekView",
                                    icon: CalendarRange,
                                },
                                {
                                    mode: "month",
                                    label: "monthView",
                                    icon: CalendarDays,
                                },
                            ] as const
                        ).map(({ mode, label, icon: Icon }) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setViewMode(mode)}
                                aria-pressed={viewMode === mode}
                                title={t(`scheduler.calendar.${label}`)}
                                aria-label={t(`scheduler.calendar.${label}`)}
                                className={`rounded-lg p-2 transition-colors ${
                                    viewMode === mode
                                        ? "bg-white text-brand-600 shadow-sm dark:bg-white/10"
                                        : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            setBookingError(null);
                            setBookingMinute(null);
                            setModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
                    >
                        <Plus className="h-4 w-4" />
                        {t("scheduler.schedule.newSession")}
                    </button>
                </div>
            </header>

            {sessionsFailed && (
                <div role="alert" className="flex items-center gap-2 text-sm">
                    <span className="text-red-600">
                        {t("scheduler.calendar.loadError")}
                    </span>
                    <button
                        type="button"
                        onClick={refetchSessions}
                        className="underline"
                    >
                        {t("scheduler.common.retry")}
                    </button>
                </div>
            )}

            <div className="glass-effect glass-border flex overflow-hidden rounded-2xl border shadow-sm xl:h-[calc(100vh-16rem)]">
                {isWeekView ? (
                    <div className="flex flex-1 flex-col overflow-hidden">
                        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 sm:px-6 dark:border-white/10">
                            <h2 className="text-sm font-semibold text-content-secondary first-letter:uppercase">
                                {weekRangeLabel()}
                            </h2>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() =>
                                        goToWeek(startOfWeekKey(todayKey))
                                    }
                                    className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                                >
                                    {t("scheduler.calendar.today")}
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.previousWeek")}
                                    onClick={() =>
                                        goToWeek(shiftDateKey(weekStart, -7))
                                    }
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.nextWeek")}
                                    onClick={() =>
                                        goToWeek(shiftDateKey(weekStart, 7))
                                    }
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                        {sessionsLoading ? (
                            <p role="status" className="p-6 text-sm">
                                {t("scheduler.common.loading")}
                            </p>
                        ) : (
                            <WeekGrid
                                weekStart={weekStart}
                                sessionsByDay={sessionsByDay}
                                availability={availabilityQuery.data ?? []}
                                todayKey={todayKey}
                                nowMinute={nowMinute}
                                newSessionMinutes={settingsQuery.data?.default_duration}
                                sessionLabel={(session) =>
                                    subjectLabel(session, patientNames) ??
                                    session.title
                                }
                                spaceName={(session) =>
                                    session.space_id
                                        ? spaceNames.get(session.space_id)
                                        : undefined
                                }
                                onCreateOnDay={(day, minute) => {
                                    revealDay(day);
                                    setBookingError(null);
                                    setBookingMinute(minute);
                                    setModalOpen(true);
                                }}
                                onCancel={(session) =>
                                    setSessionToCancel(session)
                                }
                                isCancelling={cancelSession.isPending}
                                whatsappReminder={whatsappReminder}
                                onReschedule={(session) =>
                                    openReschedule(session)
                                }
                                isRescheduling={rescheduleSession.isPending}
                            />
                        )}
                    </div>
                ) : viewMode === "month" ? (
                    <div className="flex flex-1 flex-col overflow-y-auto">
                        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 sm:px-6 dark:border-white/10">
                            <h2 className="text-sm font-semibold text-content-secondary first-letter:uppercase">
                                {currentMonth.toLocaleDateString(i18n.language, {
                                    month: "long",
                                    year: "numeric",
                                })}
                            </h2>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Today in the therapist's zone, as the grid marks it.
                                        const [year, monthNumber] = todayKey.split("-").map(Number);
                                        setCurrentMonth(new Date(year, monthNumber - 1, 1));
                                    }}
                                    className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                                >
                                    {t("scheduler.calendar.today")}
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.previousMonth")}
                                    onClick={() => shiftMonth(-1)}
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.nextMonth")}
                                    onClick={() => shiftMonth(1)}
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                        {sessionsLoading ? (
                            <p role="status" className="p-6 text-sm">
                                {t("scheduler.common.loading")}
                            </p>
                        ) : (
                            <div className="p-3 sm:p-4">
                                <MonthAgendaGrid
                                    anchor={`${month}-01`}
                                    todayKey={todayKey}
                                    bookings={visibleSessions}
                                    cardOf={(session) => {
                                        const subject =
                                            subjectLabel(session, patientNames) ?? session.title;
                                        const start = minutesOfDayTime(session.hour);
                                        const time = `${minuteLabel(start)} – ${minuteLabel(start + session.duration)}`;
                                        return {
                                            key: session.id,
                                            text: subject,
                                            title: `${time} · ${subject}`,
                                            ariaLabel: t("scheduler.calendar.openSessionDetails", {
                                                subject,
                                            }),
                                            color: BRAND_BOOKING_COLOR,
                                            past: hasHappened(session),
                                            cancelled:
                                                session.scheduled_status === ScheduledStatus.CANCELLED,
                                        };
                                    }}
                                    onOpen={(session) => setOpenedSessionId(session.id)}
                                    onShowMore={(day) => {
                                        goToWeek(startOfWeekKey(day));
                                        setViewMode("week");
                                    }}
                                    moreLabel={(count) => t("scheduler.calendar.more", { count })}
                                    onDayClick={(day) => {
                                        setSelectedDay(day);
                                        setBookingError(null);
                                        setBookingMinute(null);
                                        setModalOpen(true);
                                    }}
                                    dayClickLabel={(day) =>
                                        t("scheduler.calendar.newSessionOn", {
                                            date: formatDayHeading(day),
                                        })
                                    }
                                />
                            </div>
                        )}

                        {openedSession && (
                            <OpenedSession
                                session={openedSession}
                                day={openedSession.date.slice(0, 10)}
                                label={
                                    subjectLabel(openedSession, patientNames) ??
                                    openedSession.title
                                }
                                place={
                                    openedSession.space_id
                                        ? spaceNames.get(openedSession.space_id)
                                        : undefined
                                }
                                todayKey={todayKey}
                                nowMinute={nowMinute}
                                whatsappReminder={whatsappReminder}
                                onClose={() => setOpenedSessionId(null)}
                                onCancel={(session) => setSessionToCancel(session)}
                                isCancelling={cancelSession.isPending}
                                onReschedule={(session) => openReschedule(session)}
                                isRescheduling={rescheduleSession.isPending}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex flex-1 flex-col overflow-y-auto">
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 dark:border-white/10">
                            <h2 className="text-sm font-semibold text-content-secondary first-letter:uppercase">
                                {currentMonth.toLocaleDateString(i18n.language, {
                                    month: "long",
                                    year: "numeric",
                                })}
                            </h2>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.previousMonth")}
                                    onClick={() =>
                                        setCurrentMonth(
                                            new Date(
                                                currentMonth.getFullYear(),
                                                currentMonth.getMonth() - 1
                                            )
                                        )
                                    }
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("scheduler.calendar.nextMonth")}
                                    onClick={() =>
                                        setCurrentMonth(
                                            new Date(
                                                currentMonth.getFullYear(),
                                                currentMonth.getMonth() + 1
                                            )
                                        )
                                    }
                                    className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-end border-b border-gray-200 px-4 py-2 sm:px-6 dark:border-white/10">
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
                                <input
                                    type="checkbox"
                                    checked={hidePastSessions}
                                    onChange={(event) =>
                                        setHidePastSessions(event.target.checked)
                                    }
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-700"
                                />
                                {t("scheduler.calendar.hidePastSessions")}
                            </label>
                        </div>

                        <div className="flex flex-col gap-4 p-4 sm:p-6">
                            {sessionsLoading && (
                                <p role="status">{t("scheduler.common.loading")}</p>
                            )}

                            {sessionsLoaded && listDays.length === 0 && (
                                <p className="text-sm text-gray-500">
                                    {t(
                                        hidePastSessions && orderedDays.length > 0
                                            ? "scheduler.calendar.emptyUpcoming"
                                            : "scheduler.calendar.empty"
                                    )}
                                </p>
                            )}

                            {listDays.map((day) => (
                                <section key={day} className="flex flex-col gap-2">
                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">
                                        {formatDayHeading(day)}
                                    </h3>
                                    {(listSessionsByDay.get(day) ?? []).map(
                                        renderSession
                                    )}
                                </section>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Cancelling emails both parties and removes the calendar event,
                so it must not happen on a stray click. */}
            <ConfirmDialog
                isOpen={sessionToCancel !== null}
                destructive
                isBusy={cancelSession.isPending}
                title={t("scheduler.schedule.cancelTitle")}
                message={t("scheduler.schedule.cancelMessage", {
                    patient: sessionToCancel
                        ? (subjectLabel(sessionToCancel, patientNames) ??
                          t("scheduler.schedule.thisPatient"))
                        : t("scheduler.schedule.thisPatient"),
                    date: sessionToCancel ? sessionToCancel.date.slice(0, 10) : "",
                    time: sessionToCancel?.hour ?? "",
                })}
                confirmLabel={t("scheduler.schedule.cancelConfirm")}
                cancelLabel={t("scheduler.schedule.cancelDismiss")}
                onCancel={() => {
                    setSessionToCancel(null);
                    setRequestedByPatient(false);
                }}
                onConfirm={() => {
                    if (!sessionToCancel) return;
                    cancelSession.mutate(
                        { sessionId: sessionToCancel.id, requestedByPatient },
                        {
                            onSettled: () => {
                                setSessionToCancel(null);
                                setRequestedByPatient(false);
                            },
                        }
                    );
                }}
            >
                <div className="rounded-xl bg-surface-sunken p-3">
                    <Checkbox
                        id="cancel-requested-by-patient"
                        checked={requestedByPatient}
                        onChange={setRequestedByPatient}
                        label={t("reports.cancel.requestedByPatient")}
                    />
                </div>
            </ConfirmDialog>
            <ScheduleSessionModal
                alchelyMeetingAvailable={alchelyMeetingAvailable}
                openSessionByDefault={openSessionByDefault}
                courses={mentoringCourses}
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    // The day a slot or a month cell picked is for that booking
                    // only; the header's button opens on today again.
                    setSelectedDay(null);
                }}
                settings={settingsQuery.data}
                spaces={spacesQuery.data ?? []}
                patients={bookableOptions}
                initialDate={selectedDay ?? toDateKey(new Date(), timezone ?? "UTC")}
                initialMinute={bookingMinute}
                isSaving={scheduleSession.isPending}
                errorMessage={bookingError}
                onSubmit={(payload) => {
                    setBookingError(null);
                    scheduleSession.mutate(payload, {
                        onSuccess: (response) => {
                            setModalOpen(false);
                            // The booked day is often not the one on screen —
                            // the modal picks the next day with availability —
                            // so show the session that was just created.
                            const created = response?.data;
                            if (created?.date) revealDay(created.date);
                        },
                        onError: (error: unknown) => {
                            const status = (
                                error as { response?: { status?: number } }
                            )?.response?.status;
                            setBookingError(
                                status === 409
                                    ? t("scheduler.schedule.slotTaken")
                                    : t("scheduler.schedule.scheduleError")
                            );
                        },
                    });
                }}
            />

            {/* Both parties are emailed on save, so the therapist confirms a
                new time here rather than dragging a block by accident. */}
            {sessionToReschedule && settingsQuery.data && (
                <RescheduleSessionModal
                    session={sessionToReschedule}
                    settings={settingsQuery.data}
                    subjectLabel={
                        sessionToReschedule
                            ? subjectLabel(sessionToReschedule, patientNames)
                            : null
                    }
                    isSaving={rescheduleSession.isPending}
                    errorMessage={rescheduleError}
                    onClose={() => setSessionToReschedule(null)}
                    onSubmit={(start) => {
                        if (!sessionToReschedule) return;
                        setRescheduleError(null);
                        rescheduleSession.mutate(
                            { sessionId: sessionToReschedule.id, start },
                            {
                                onSuccess: (response) => {
                                    setSessionToReschedule(null);
                                    // The new day is often not the one on
                                    // screen, so follow the session there.
                                    const moved = response?.data;
                                    if (moved?.date) revealDay(moved.date);
                                },
                                onError: (error: unknown) => {
                                    const status = (
                                        error as {
                                            response?: { status?: number };
                                        }
                                    )?.response?.status;
                                    setRescheduleError(
                                        status === 409
                                            ? t("scheduler.schedule.slotTaken")
                                            : t(
                                                  "scheduler.schedule.rescheduleError"
                                              )
                                    );
                                },
                            }
                        );
                    }}
                />
            )}
        </div>
    );
}
